/*
 * Cart upsell card.
 *
 * The radios are the control surface for a real cart line, not a form that
 * gets read at checkout: picking an option writes to the cart immediately and
 * re-renders the drawer, so the subtotal moves with it.
 *
 * State lives in the cart, never in this file. Every render re-derives which
 * radio is checked from the line item (see isaboko-cart-upsell.liquid), so a
 * refresh, a quantity change, or a removal from the item list can't leave the
 * card out of sync.
 */
if (!customElements.get('isaboko-cart-upsell')) {
  // Every cart write re-renders the drawer from Liquid, which would otherwise
  // reset the pane to the merchant's default each time. Remember what the
  // customer chose for the rest of their visit instead.
  const OPEN_STATE_KEY = 'isaboko:cart-upsell-open';

  const readOpenState = () => {
    try {
      return sessionStorage.getItem(OPEN_STATE_KEY);
    } catch (error) {
      return null;
    }
  };

  const writeOpenState = (open) => {
    try {
      sessionStorage.setItem(OPEN_STATE_KEY, open ? '1' : '0');
    } catch (error) {
      // Private browsing — the pane just falls back to the default state.
    }
  };

  customElements.define(
    'isaboko-cart-upsell',
    class IsabokoCartUpsell extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.pending = false;
        this.addEventListener('change', this.onChange.bind(this));
        this.restoreOpenState();
      }

      restoreOpenState() {
        const pane = this.querySelector('details');
        if (!pane) return;

        const stored = readOpenState();
        if (stored !== null) pane.open = stored === '1';

        pane.addEventListener('toggle', () => writeOpenState(pane.open));
      }

      get sectionId() {
        return this.dataset.sectionId;
      }

      onChange(event) {
        const input = event.target;
        if (!input.matches('input[type="radio"]')) return;

        // A second change while a write is in flight would race the re-render.
        if (this.pending) {
          input.checked = false;
          const current = this.querySelector(`input[value="${this.dataset.selected}"]`);
          if (current) current.checked = true;
          return;
        }

        this.apply(input.value);
      }

      async apply(option) {
        const lineKey = this.dataset.lineKey;

        // Declining something that was never added is a no-op. Bail before
        // going into the loading state, or the card stays disabled forever.
        if (option === 'decline' && !lineKey) {
          this.dataset.selected = option;
          return;
        }

        this.setLoading(true);
        this.hideError();

        const sections = [this.sectionId, 'cart-icon-bubble'];

        try {
          // Switching between one-time and subscribe is an add plus a remove:
          // Shopify treats a different selling plan as a different line.
          //
          // Add first, deliberately. If the add fails — sold out, quantity
          // rules — the customer still has what they had. Removing first would
          // lose their line to a call that then never succeeds.
          let response = null;

          if (option !== 'decline') {
            const line = {
              id: Number(this.dataset.variantId),
              quantity: 1,
              properties: { _upsell: option },
            };
            if (option === 'subscribe' && this.dataset.sellingPlanId) {
              line.selling_plan = Number(this.dataset.sellingPlanId);
            }

            response = await this.post(window.routes.cart_add_url, {
              items: [line],
              // Only worth rendering if no removal follows.
              sections: lineKey ? undefined : sections,
              sections_url: window.location.pathname,
            });
          }

          if (lineKey) {
            response = await this.post(window.routes.cart_change_url, {
              id: lineKey,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            });
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            const isEmpty = option === 'decline' && response.item_count === 0;
            cartDrawer.classList.toggle('is-empty', isEmpty);
          }

          // If the swap did not happen this element is still on screen, so it
          // has to be handed back to the customer rather than left disabled.
          if (!this.renderSections(response.sections)) {
            this.dataset.selected = option;
            this.setLoading(false);
          }
        } catch (error) {
          console.error(error);
          this.showError(error.message);
          this.setLoading(false);
        }
      }

      async post(url, body) {
        const response = await fetch(url, {
          ...fetchConfig(),
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || window.cartStrings?.error || 'Cart update failed');
        }
        return data;
      }

      /*
       * Swap the whole drawer body rather than just this card: the line item
       * list, the subtotal and this card all changed, and they have to change
       * together. Mirrors what cart-drawer.js does after an add to cart.
       */
      renderSections(sections) {
        if (!sections) return false;

        const parse = (html, selector) => {
          if (!html) return null;
          return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        };

        const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
        const source = parse(sections[this.sectionId], '.drawer__inner');
        if (!drawerInner || !source) return false;

        drawerInner.innerHTML = source.innerHTML;

        const bubble = document.getElementById('cart-icon-bubble');
        const bubbleSource = parse(sections['cart-icon-bubble'], '.shopify-section');
        if (bubble && bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;

        return true;
      }

      setLoading(loading) {
        this.pending = loading;
        this.classList.toggle('isaboko-cart-upsell--loading', loading);
        this.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.disabled = loading;
        });
      }

      showError(message) {
        const target = this.querySelector('.isaboko-cart-upsell__error');
        if (!target) return;
        target.textContent = message;
        target.hidden = false;
      }

      hideError() {
        const target = this.querySelector('.isaboko-cart-upsell__error');
        if (!target) return;
        target.textContent = '';
        target.hidden = true;
      }
    }
  );
}
